import { useRef, useState } from "react";
import { motion } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.16, duration: 1.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function AccessGate({ onUnlock }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [status, setStatus] = useState("idle"); // idle | checking | denied
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const verify = async (code) => {
    setStatus("checking");
    try {
      const res = await fetch(`${API}/api/access/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (data.granted) {
        localStorage.setItem("mip-access", "granted");
        onUnlock();
        return;
      }
    } catch (e) {
      /* fall through to denied */
    }
    setStatus("denied");
    setDigits(["", "", "", ""]);
    setTimeout(() => refs[0].current?.focus(), 60);
    setTimeout(() => setStatus("idle"), 900);
  };

  const handleChange = (i, val) => {
    const v = val.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 3) refs[i + 1].current?.focus();
    if (v && i === 3) verify(next.join(""));
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs[i - 1].current?.focus();
  };

  return (
    <motion.div
      data-testid="access-gate"
      className="fixed inset-0 z-[80] archival-vignette grain overflow-y-auto archival-scroll"
      exit={{ clipPath: "inset(0 0 100% 0)", transition: { duration: 1.3, ease: [0.76, 0, 0.24, 1] } }}
    >
      <div className="min-h-full flex flex-col items-center justify-center px-6 py-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="font-nav text-[10px] tracking-[0.5em] text-[#857565] uppercase mb-8"
          data-testid="gate-eyebrow"
        >
          A Private Presentation · By Invitation Only
        </motion.p>

        <h1 className="text-center" data-testid="gate-title">
          <span className="mask-line">
            <motion.span
              custom={0}
              variants={lineVariants}
              initial="hidden"
              animate="show"
              className="block font-display uppercase text-[#E8DCC4] text-4xl sm:text-5xl lg:text-6xl tracking-[0.08em]"
            >
              Miniature
            </motion.span>
          </span>
          <span className="mask-line mt-1">
            <motion.span
              custom={1}
              variants={lineVariants}
              initial="hidden"
              animate="show"
              className="block font-display uppercase text-[#E8DCC4] text-4xl sm:text-5xl lg:text-6xl tracking-[0.08em]"
            >
              India Park
            </motion.span>
          </span>
          <span className="mask-line mt-5">
            <motion.span
              custom={2}
              variants={lineVariants}
              initial="hidden"
              animate="show"
              className="block font-display uppercase text-[#C5A059] text-base sm:text-lg tracking-[0.65em] pl-2"
            >
              Bharat
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.15, duration: 1 }}
          className="ornament my-10"
        >
          <span className="diamond" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-xl text-center"
          data-testid="confidentiality-notice"
        >
          <h2 className="font-nav text-[10px] tracking-[0.45em] text-[#C5A059] uppercase mb-6">
            Confidential &amp; Proprietary
          </h2>
          <div className="font-serif-body text-[#B8A795] text-base sm:text-lg leading-relaxed space-y-4">
            <p>
              This presentation contains private, confidential, and proprietary information
              intended solely for authorized recipients.
            </p>
            <p>
              The concepts, plans, visuals, research, and supporting materials contained in this
              presentation are provided for private review only.
            </p>
            <p>
              No part of this material may be copied, reproduced, forwarded, published,
              distributed, disclosed, or shared with any third party without prior authorization.
            </p>
          </div>
          <a
            data-testid="gate-titanium-link"
            href="https://titaniumwealthusa.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-6 font-nav text-[10px] tracking-[0.3em] uppercase text-[#C5A059] border border-[rgba(197,160,89,0.3)] px-6 py-3 hover:border-[rgba(197,160,89,0.65)] hover:bg-[rgba(197,160,89,0.06)] transition-all duration-500"
          >
            TitaniumWealthUSA.com
          </a>
          <div className="ornament mt-8">
            <span className="diamond" />
          </div>
          <p className="font-nav text-[10px] tracking-[0.45em] uppercase text-[#857565] mt-6">
            Private Presentation
          </p>
          <p className="font-nav text-[10px] tracking-[0.25em] text-[#C5A059] mt-2">
            RajGoyal.com
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className={`mt-12 flex flex-col items-center ${status === "denied" ? "shake" : ""}`}
        >
          <p className="font-nav text-[10px] tracking-[0.4em] text-[#857565] uppercase mb-5">
            Enter your access code
          </p>
          <div className="flex gap-3" data-testid="access-code-inputs">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={refs[i]}
                data-testid={`code-cell-${i}`}
                className="code-cell"
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={d}
                aria-label={`Code digit ${i + 1}`}
                autoFocus={i === 0}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
              />
            ))}
          </div>
          <p
            data-testid="access-feedback"
            className={`font-serif-body italic text-sm mt-5 h-5 transition-opacity duration-500 ${
              status === "denied" ? "text-[#9E472A] opacity-100" : "opacity-0"
            }`}
          >
            This code does not open the archive.
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
}
